import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  commonSaveData,
  commonSaveDataOne,
  initCommonState,
} from "../../../../redux/slices/commonSlice";
import { payrollProcessPath } from "../../../../services/config";
import { fetchBranchData } from "../../../../redux/slices/branchSlice";

const C = {
  bg:               "var(--pay-bg)",
  surface:          "var(--pay-surface)",
  surfaceSecondary: "var(--pay-surface-2)",
  border:           "var(--pay-border)",
  text:             "var(--pay-text)",
  muted:            "var(--pay-text-muted)",
  purple:           "#8660F8",
};

const card = {
  background:   C.surface,
  border:       `1px solid ${C.border}`,
  borderRadius: 12,
  padding:      16,
};

const input = {
  width:        "100%",
  background:   C.surfaceSecondary,
  border:       `1px solid ${C.border}`,
  borderRadius: 8,
  padding:      "8px 12px",
  fontSize:     12,
  color:        C.text,
  outline:      "none",
  boxSizing:    "border-box",
};

const lbl      = { fontSize: 11, color: C.muted, display: "block", marginBottom: 6 };
const hSection = { fontSize: 13, fontWeight: 700, margin: "0 0 14px", display: "flex", alignItems: "center", gap: 8 };

const PILL_TOGGLE = ({ on, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    style={{
      width: 56, height: 28, borderRadius: 14,
      border: `1px solid ${on ? C.purple : C.border}`,
      background: on ? C.purple : C.surfaceSecondary,
      cursor: "pointer", position: "relative", padding: 0, flexShrink: 0,
    }}
    aria-pressed={on}
  >
    <span style={{
      position: "absolute", top: 3, left: on ? 30 : 3,
      width: 20, height: 20, borderRadius: "50%",
      background: "#fff", transition: "left 0.2s",
    }} />
  </button>
);

// ─── ToggleRow — now properly wired via checked/onChange ──────────────────────
function ToggleRow({ checked, onChange, label, sub }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ width: 20, height: 20, accentColor: C.purple }}
      />
      <div>
        <p style={{ fontSize: 12, fontWeight: 500, color: C.text, margin: "0 0 4px" }}>{label}</p>
        <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>{sub}</p>
      </div>
    </label>
  );
}

function CategoryCard({ title, icon, iconColor, iconBg, monthly, onMonthly, enabled, onEnabled, perDay, onPerDay, maxClaims, onMaxClaims }) {
  return (
    <div style={{ ...card, padding: 14, background: C.surfaceSecondary }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className={`fa-solid ${icon}`} style={{ color: iconColor, fontSize: 18 }} />
          </div>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h4>
        </div>
        <PILL_TOGGLE on={enabled} onChange={onEnabled} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Monthly limit</label>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.text, fontSize: 15 }}>₹</span>
            <input value={monthly} onChange={e => onMonthly(e.target.value)} style={{ ...input, paddingLeft: 28, paddingTop: 10, paddingBottom: 10 }} />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Per day cap (optional)</label>
          <input value={perDay} onChange={e => onPerDay(e.target.value)} placeholder="No limit" style={input} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Max claims/month</label>
          <input value={maxClaims} onChange={e => onMaxClaims(e.target.value)} placeholder="Unlimited" style={input} />
        </div>
      </div>
    </div>
  );
}

const DEFAULT_CATS = {
  Travel: { enabled: true, monthly: "2500", perDay: "", maxClaims: "" },
  Food:   { enabled: true, monthly: "1500", perDay: "", maxClaims: "" },
  Fuel:   { enabled: true, monthly: "3000", perDay: "", maxClaims: "" },
  Other:  { enabled: true, monthly: "1000", perDay: "", maxClaims: "" },
};

const CAT_KEYS = ["Travel", "Food", "Fuel", "Other"];

const toLimitString    = val => String(val ?? "").replace(/[₹,\s]/g, "");
const normalizeCategory = cat => ({
  enabled:   Boolean(cat?.enabled),
  monthly:   toLimitString(cat?.monthly),
  perDay:    toLimitString(cat?.perDay),
  maxClaims: toLimitString(cat?.maxClaims),
});
const buildCategoriesPayload = cats =>
  CAT_KEYS.reduce((acc, key) => {
    acc[key] = normalizeCategory(cats?.[key] || DEFAULT_CATS[key]);
    return acc;
  }, {});
const buildAppliesToPayload = appliesTo => ({
  type:  appliesTo?.type  || "all",
  value: appliesTo?.value || "All Employees",
});
const getDefaultEffectiveFrom = () => new Date().toISOString().slice(0, 10);

// ─── Default approval rule state ──────────────────────────────────────────────
const DEFAULT_APPROVAL_RULES = {
  receiptMandatory: true,
  gpsMandatory:     true,
  duplicateDetect:  true,
};

// ─── PolicyLimitsEditor ───────────────────────────────────────────────────────
// NEW PROP: contextEmployee — shows which employee triggered the open (from PolicyBadge)
const PolicyLimitsEditor = ({
  onBack,
  onSaved,
  policyGroups    = [],
  initialGroupKey = null,
  initialPolicyKey = "",
  contextEmployee  = null,   // NEW — passed from PolicyDashboard
}) => {
  const dispatch    = useDispatch();
  const commonStore = useSelector(s => s.common);
  const branchList  = useSelector(s => s.branch?.brnGetData || []);
  const user        = JSON.parse(localStorage.getItem("user_data")) || {};

  const policyActionRef        = useRef(null);
  const employeesFetchRef      = useRef(false);
  const assignPendingRef       = useRef(false);
  const assignRevertRef        = useRef(null);
  const openAssignAfterSaveRef = useRef(false);

  const [policyName,     setPolicyName]     = useState("Standard Policy");
  const [policyActive,   setPolicyActive]   = useState(true);
  const [appliesTo,      setAppliesTo]      = useState({ type: "all", value: "All Employees" });
  const [branch,         setBranch]         = useState("all");
  const [effectiveFrom,  setEffectiveFrom]  = useState("");
  const [monthlyCap,     setMonthlyCap]     = useState("6000");
  const [categories,     setCategories]     = useState(DEFAULT_CATS);
  const [changeHistory,  setChangeHistory]  = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [limitsLoading,  setLimitsLoading]  = useState(false);
  const [activeGroup,    setActiveGroup]    = useState(initialGroupKey);

  // ── Wired approval rules (was hardcoded checked/onChange={() => {}}) ────────
  const [approvalRules, setApprovalRules] = useState(DEFAULT_APPROVAL_RULES);
  const [managerThreshold,  setManagerThreshold]  = useState("Required above ₹2,000");
  const [financeThreshold,  setFinanceThreshold]  = useState("Required above ₹5,000");

  // ── Exception tags ────────────────────────────────────────────────────────
  const [exceptionTags, setExceptionTags] = useState([
    { t: "Night Shift Meal Allowance", c: C.purple    },
    { t: "Client Visit Override",      c: "#3b82f6"   },
    { t: "Emergency Fuel Override",    c: "#f59e0b"   },
    { t: "Special Approval Only",      c: "#ef4444"   },
  ]);
  const removeException = idx => setExceptionTags(prev => prev.filter((_, i) => i !== idx));

  // ── Impact analysis & alerts from API ────────────────────────────────────
  const [impactData, setImpactData] = useState(null);
  const [policyAlerts, setPolicyAlerts] = useState([]);

  const [showAssignModal,  setShowAssignModal]  = useState(false);
  const [empSearch,        setEmpSearch]        = useState("");
  const [employees,        setEmployees]        = useState([]);
  const [policyKey,        setPolicyKey]        = useState("");
  const [empLoading,       setEmpLoading]       = useState(false);
  const [assigningStaffId, setAssigningStaffId] = useState(null);
  const [assignedCount,    setAssignedCount]    = useState(0);

  const resetPolicyForm = useCallback(() => {
    setPolicyName("Standard Policy");
    setPolicyActive(true);
    setAppliesTo({ type: "all", value: "All Employees" });
    setActiveGroup(initialGroupKey || "all");
    setBranch("all");
    setEffectiveFrom(getDefaultEffectiveFrom());
    setMonthlyCap("6000");
    setCategories(DEFAULT_CATS);
    setChangeHistory([]);
    setPolicyKey("");
    setEmployees([]);
    setAssignedCount(0);
    setEmpSearch("");
    setShowAssignModal(false);
    setAssigningStaffId(null);
    setApprovalRules(DEFAULT_APPROVAL_RULES);
    setImpactData(null);
    setPolicyAlerts([]);
    assignPendingRef.current        = false;
    assignRevertRef.current         = null;
    openAssignAfterSaveRef.current  = false;
    employeesFetchRef.current       = false;
  }, [initialGroupKey]);

  const applyPolicyFromApi = useCallback(d => {
    if (!d || d.policyName === undefined) return;
    setPolicyName(d.policyName || "");
    setPolicyActive(Boolean(d.policyActive));
    setAppliesTo(d.appliesTo || { type: "all", value: "All Employees" });
    setBranch(d.branch || "all");
    setEffectiveFrom(d.effectiveFrom || "");
    setMonthlyCap(String(d.monthlyLimit ?? "6000"));
    if (d.categories)    setCategories({ ...DEFAULT_CATS, ...d.categories });
    if (Array.isArray(d.changeHistory)) setChangeHistory(d.changeHistory);
    // ── Populate approval rules from API if provided ──
    if (d.approvalRules) setApprovalRules({ ...DEFAULT_APPROVAL_RULES, ...d.approvalRules });
    if (d.managerThreshold) setManagerThreshold(d.managerThreshold);
    if (d.financeThreshold) setFinanceThreshold(d.financeThreshold);
    if (Array.isArray(d.exceptionTags)) setExceptionTags(d.exceptionTags);
    // ── Impact & alerts ──
    if (d.impactData)   setImpactData(d.impactData);
    if (Array.isArray(d.policyAlerts)) setPolicyAlerts(d.policyAlerts);
    setPolicyKey(d.policyKey || (d.policyName && d.effectiveFrom ? `${d.policyName}_${d.effectiveFrom}` : ""));
  }, []);

  const loadPolicyLimits = useCallback((requestedPolicyKey = "") => {
    setLimitsLoading(true);
    policyActionRef.current = "fetchPolicyLimits";
    dispatch(commonSaveData({
      path: payrollProcessPath,
      data: {
        action:    "fetchPolicyLimits",
        userId:    Number(user.user_id),
        policyKey: requestedPolicyKey || initialPolicyKey || policyKey,
      },
    }));
  }, [dispatch, initialPolicyKey, policyKey, user.user_id]);

  const getSavedPolicyKey = useCallback(
    () => policyKey || initialPolicyKey || "",
    [policyKey, initialPolicyKey]
  );

  useEffect(() => {
    setLimitsLoading(false);
    policyActionRef.current = null;
    resetPolicyForm();

    if (initialPolicyKey) {
      loadPolicyLimits(initialPolicyKey);
      return;
    }
  }, [initialPolicyKey, initialGroupKey]); // eslint-disable-line

  useEffect(() => {
    if (user.user_id) dispatch(fetchBranchData());
  }, [dispatch, user.user_id]);

  useEffect(() => {
    if (!initialGroupKey || !policyGroups.length) return;
    const g = policyGroups.find(x => x.key === initialGroupKey);
    if (g) selectGroup(g);
  }, [initialGroupKey, policyGroups]); // eslint-disable-line

  useEffect(() => {
    if (!commonStore.commonSuccess && !commonStore.commonError) return;

    if (commonStore.commonSuccess) {
      if (saving) {
        toast.success(commonStore.commonMessage || "Policy limits saved successfully");
        setSaving(false);
        const savedPolicyKey = commonStore.commonData?.policyKey || `${policyName.trim()}_${effectiveFrom}`;
        setPolicyKey(savedPolicyKey);
        policyActionRef.current = null;
        dispatch(initCommonState());
        if (openAssignAfterSaveRef.current) {
          openAssignAfterSaveRef.current = false;
          toast.success("Policy saved. Select employees to assign.");
          setShowAssignModal(true);
          return;
        }
        if (typeof onSaved === "function") onSaved(savedPolicyKey);
        return;
      }
      if (limitsLoading) {
        applyPolicyFromApi(commonStore.commonData);
        setAssignedCount(0);
        setLimitsLoading(false);
        policyActionRef.current = null;
        dispatch(initCommonState());
        return;
      }
    }

    if (commonStore.commonError) {
      if (saving)       toast.error(commonStore.commonMessage || "Failed to save policy");
      if (limitsLoading) setLimitsLoading(false);
      if (saving)        setSaving(false);
      policyActionRef.current = null;
      dispatch(initCommonState());
    }
  }, [
    commonStore.commonSuccess,
    commonStore.commonError,
    commonStore.commonData,
    commonStore.commonMessage,
    saving,
    limitsLoading,
    applyPolicyFromApi,
    onSaved,
    dispatch,
  ]); // eslint-disable-line

  const loadEmployees = useCallback(() => {
    setEmpLoading(true);
    employeesFetchRef.current = true;
    dispatch(commonSaveDataOne({
      path: payrollProcessPath,
      data: {
        action:       "fetchEmployeesForPolicy",
        userId:       Number(user.user_id),
        branchId:     branch === "all" ? "all" : String(branch),
        departmentId: "all",
        shiftId:      "all",
        search:       empSearch,
        policyKey:    getSavedPolicyKey(),
      },
    }));
  }, [dispatch, user.user_id, branch, empSearch, getSavedPolicyKey]);

  useEffect(() => {
    if (!showAssignModal) return undefined;
    const t = setTimeout(() => loadEmployees(), 300);
    return () => clearTimeout(t);
  }, [showAssignModal, empSearch, loadEmployees]);

  useEffect(() => {
    if (!commonStore.commonSuccessOne && !commonStore.commonErrorOne) return;

    if (assignPendingRef.current) {
      if (commonStore.commonSuccessOne) {
        const nextCount = commonStore.commonDataOne?.assignedCount;
        if (Number.isFinite(Number(nextCount))) setAssignedCount(Number(nextCount));
        const didAssign = assignRevertRef.current ? !assignRevertRef.current.wasAssigned : true;
        toast.success(commonStore.commonMessageOne || `Employee ${didAssign ? "assigned" : "unassigned"} successfully`);
        assignRevertRef.current = null;
        setAssigningStaffId(null);
        assignPendingRef.current  = false;
        employeesFetchRef.current = true;
        dispatch(commonSaveDataOne({
          path: payrollProcessPath,
          data: {
            action:       "fetchEmployeesForPolicy",
            userId:       Number(user.user_id),
            branchId:     branch === "all" ? "all" : String(branch),
            departmentId: "all",
            shiftId:      "all",
            search:       empSearch,
            policyKey:    getSavedPolicyKey(),
          },
        }));
      } else if (commonStore.commonErrorOne && assignRevertRef.current) {
        const { staffId, wasAssigned } = assignRevertRef.current;
        setEmployees(prev => prev.map(e =>
          Number(e.staffId) === Number(staffId) ? { ...e, assigned: wasAssigned } : e
        ));
        setAssignedCount(prev => Math.max(0, prev + (wasAssigned ? 1 : -1)));
        toast.error(commonStore.commonMessageOne || "Assignment failed");
        setAssigningStaffId(null);
        assignPendingRef.current = false;
        assignRevertRef.current  = null;
        dispatch(initCommonState());
      }
      if (commonStore.commonSuccessOne) dispatch(initCommonState());
      return;
    }

    if (!employeesFetchRef.current) return;
    if (commonStore.commonSuccessOne) {
      const d = commonStore.commonDataOne;
      if (Array.isArray(d?.employees)) {
        setEmployees(d.employees);
        setAssignedCount(d.employees.filter(emp => emp.assigned).length);
      }
      if (d?.policyKey) setPolicyKey(d.policyKey);
      setEmpLoading(false);
      employeesFetchRef.current = false;
      dispatch(initCommonState());
    }
    if (commonStore.commonErrorOne) {
      setEmpLoading(false);
      employeesFetchRef.current = false;
      toast.error(commonStore.commonMessageOne || "Failed to load employees");
      dispatch(initCommonState());
    }
  }, [
    commonStore.commonSuccessOne,
    commonStore.commonErrorOne,
    commonStore.commonDataOne,
    commonStore.commonMessageOne,
    dispatch, branch, empSearch, user.user_id, getSavedPolicyKey,
  ]);

  const buildSavePayload = () => ({
    action: "savePolicyLimits",
    userId: Number(user.user_id),
    policyData: {
      policyName:    policyName.trim(),
      policyKey:     policyKey || undefined,
      policyActive:  Boolean(policyActive),
      appliesTo:     buildAppliesToPayload(appliesTo),
      branch:        branch || "all",
      effectiveFrom,
      monthlyLimit:  toLimitString(monthlyCap),
      categories:    buildCategoriesPayload(categories),
      approvalRules: {
        ...approvalRules,
        managerThreshold,
        financeThreshold,
      },
      exceptionTags: exceptionTags.map(e => e.t),
    },
  });

  const handleSave = () => {
    if (!policyName.trim()) { toast.error("Please enter a policy name"); return; }
    if (!effectiveFrom)     { toast.error("Please select an effective date"); return; }
    setSaving(true);
    policyActionRef.current = "savePolicyLimits";
    dispatch(commonSaveData({ path: payrollProcessPath, data: buildSavePayload() }));
  };

  const openAssignModal = () => {
    const savedKey = getSavedPolicyKey();
    if (!savedKey) {
      if (!policyName.trim()) { toast.error("Please enter a policy name before assigning employees"); return; }
      if (!effectiveFrom)     { toast.error("Please select an effective date before assigning employees"); return; }
      openAssignAfterSaveRef.current = true;
      setSaving(true);
      policyActionRef.current = "savePolicyLimits";
      dispatch(commonSaveData({ path: payrollProcessPath, data: buildSavePayload() }));
      return;
    }
    if (!policyKey) setPolicyKey(savedKey);
    setShowAssignModal(true);
  };

  const handleToggleAssign = (staffId, currentlyAssigned) => {
    const key = getSavedPolicyKey();
    if (!key) { toast.error("Save policy first to get a policy key for assignment"); return; }
    if (!policyKey) setPolicyKey(key);
    if (assignPendingRef.current) return;
    const sid = Number(staffId);
    assignRevertRef.current  = { staffId: sid, wasAssigned: currentlyAssigned };
    setAssigningStaffId(sid);
    assignPendingRef.current = true;
    employeesFetchRef.current = false;
    setEmployees(prev => prev.map(e =>
      Number(e.staffId) === sid ? { ...e, assigned: !currentlyAssigned } : e
    ));
    setAssignedCount(prev => Math.max(0, prev + (currentlyAssigned ? -1 : 1)));
    dispatch(commonSaveDataOne({
      path: payrollProcessPath,
      data: {
        action:    "assignPolicyToEmployees",
        userId:    Number(user.user_id),
        staffIds:  [sid],
        policyKey: key,
        assign:    !currentlyAssigned,
      },
    }));
  };

  const selectGroup = g => {
    setActiveGroup(g.key);
    setAppliesTo({ type: g.type || "designation", value: g.label, id: g.id });
  };

  const groupsList = policyGroups.length
    ? policyGroups
    : [{ key: "all", label: "All Employees", count: "—", type: "all" }];

  const catChange = (key, field, val) =>
    setCategories(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));

  const btnOutline = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "8px 14px", color: C.text, fontSize: 12, fontWeight: 500, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
  const btnPrimary = {
    ...btnOutline, background: C.purple, border: "none", color: "#fff",
    boxShadow: "0 0 20px rgba(134,96,248,0.15)",
  };

  return (
    <div style={{ background: C.bg, minHeight: "100%", fontFamily: "'Outfit', sans-serif", color: C.text, padding: "24px 32px 40px" }}>

      {/* ── Context banner — shows which employee opened this ── */}
      {contextEmployee && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: "rgba(134,96,248,0.08)", border: `1px solid rgba(134,96,248,0.2)`, borderRadius: 10, fontSize: 12, color: C.muted }}>
          <i className="fa-solid fa-user" style={{ color: C.purple, marginRight: 8 }} />
          Viewing policy for: <strong style={{ color: C.text }}>{contextEmployee.staffName || contextEmployee.employeeId}</strong>
          {contextEmployee.department && <> · {contextEmployee.department}</>}
        </div>
      )}

      {/* ── Header ── */}
      <section style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 24, marginBottom: 32, paddingBottom: 24, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <button type="button" onClick={onBack} style={{ ...btnOutline, padding: "10px 14px" }} title="Back to policy list">
            <i className="fa-solid fa-arrow-left" style={{ fontSize: 12 }} />
          </button>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px", lineHeight: 1.2, color: C.text }}>Reimbursement Policy Limits</h1>
            <p style={{ fontSize: 12, color: C.text, margin: 0, opacity: 0.75 }}>Manage monthly caps, category limits, designation rules and exceptions.</p>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <button type="button" onClick={openAssignModal} style={btnOutline}>
            <i className="fa-solid fa-users-gear" /> Assign Employees
          </button>
          <button type="button" onClick={handleSave} disabled={saving || limitsLoading} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
            <i className="fa-solid fa-save" /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 24 }}>

        {/* ── Left column ── */}
        <div style={{ gridColumn: "span 3", display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{ position: "relative" }}>
            <i className="fa-solid fa-search" style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14 }} />
            <input type="search" placeholder="Search policies..." style={{ ...input, paddingLeft: 44 }} />
          </div>
          <div style={card}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 16px" }}>Policy groups</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {groupsList.map((g, i) => {
                const isActive = activeGroup === g.key;
                return (
                  <button key={g.key || i} type="button" onClick={() => selectGroup(g)}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 8, border: isActive ? `1px solid rgba(134,96,248,0.3)` : `1px solid transparent`, background: isActive ? "rgba(134,96,248,0.1)" : C.surfaceSecondary, color: C.text, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{g.label}</span>
                    <span style={{ fontSize: 11, color: isActive ? C.purple : C.muted, fontWeight: isActive ? 600 : 400 }}>{g.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Center column ── */}
        <div style={{ gridColumn: "span 6", display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Policy information */}
          <div style={card}>
            <h3 style={hSection}><i className="fa-solid fa-info-circle" style={{ color: C.purple }} /> Policy information</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <label style={lbl}>Policy name</label>
                <input value={policyName} onChange={e => setPolicyName(e.target.value)} style={input} />
              </div>
              <div>
                <label style={lbl}>Applies to</label>
                <select value={appliesTo.value}
                  onChange={e => {
                    const val = e.target.value;
                    const grp = groupsList.find(x => x.label === val);
                    setAppliesTo({ type: val === "All Employees" ? "all" : (grp?.type || "designation"), value: val, id: grp?.id });
                  }} style={input}>
                  <option value="All Employees">All Employees</option>
                  {groupsList.filter(g => g.type !== "all").map(g => (
                    <option key={g.key} value={g.label}>{g.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Branch</label>
                <select value={branch} onChange={e => setBranch(e.target.value)} style={input}>
                  <option value="all">All Branches</option>
                  {(branchList || []).map(b => (
                    <option key={b.branchId} value={String(b.branchId)}>{b.branch_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Effective from</label>
                <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} style={input} />
              </div>
            </div>
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, margin: "0 0 4px" }}>Policy status</p>
                <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>Toggle to activate or deactivate this policy</p>
              </div>
              <PILL_TOGGLE on={policyActive} onChange={setPolicyActive} />
            </div>
          </div>

          {/* Monthly cap */}
          <div style={card}>
            <h3 style={hSection}><i className="fa-solid fa-calendar-days" style={{ color: C.purple }} /> Overall monthly employee cap</h3>
            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Monthly limit</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", fontWeight: 600 }}>₹</span>
                <input value={monthlyCap} onChange={e => setMonthlyCap(e.target.value)} style={{ ...input, borderColor: C.purple, paddingLeft: 32, paddingRight: 100 }} />
                <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: C.muted }}>per month</span>
              </div>
            </div>
          </div>

          {/* Category limits */}
          <div style={card}>
            <h3 style={hSection}><i className="fa-solid fa-layer-group" style={{ color: C.purple }} /> Category limits</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {CAT_KEYS.map(key => {
                const meta = {
                  Travel: { icon: "fa-plane",    iconColor: "#3b82f6", iconBg: "rgba(59,130,246,0.1)"  },
                  Food:   { icon: "fa-utensils", iconColor: "#22c55e", iconBg: "rgba(34,197,94,0.1)"   },
                  Fuel:   { icon: "fa-gas-pump", iconColor: "#f59e0b", iconBg: "rgba(245,158,11,0.1)"  },
                  Other:  { icon: "fa-ellipsis", iconColor: "#a855f7", iconBg: "rgba(168,85,247,0.1)"  },
                }[key];
                const cat = categories[key] || DEFAULT_CATS[key];
                return (
                  <CategoryCard key={key} title={key} icon={meta.icon} iconColor={meta.iconColor} iconBg={meta.iconBg}
                    monthly={cat.monthly || ""} onMonthly={v => catChange(key, "monthly", v)}
                    enabled={Boolean(cat.enabled)} onEnabled={v => catChange(key, "enabled", v)}
                    perDay={cat.perDay || ""} onPerDay={v => catChange(key, "perDay", v)}
                    maxClaims={cat.maxClaims || ""} onMaxClaims={v => catChange(key, "maxClaims", v)}
                  />
                );
              })}
            </div>
          </div>

          {/* Approval rules */}
          <div style={card}>
            <h3 style={hSection}><i className="fa-solid fa-shield-halved" style={{ color: C.purple }} /> Approval rules</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 8 }}>Manager approval threshold</label>
                <select value={managerThreshold} onChange={e => setManagerThreshold(e.target.value)} style={input}>
                  <option>Required above ₹2,000</option>
                  <option>Required above ₹1,000</option>
                  <option>Required above ₹5,000</option>
                  <option>Always required</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 8 }}>Finance approval threshold</label>
                <select value={financeThreshold} onChange={e => setFinanceThreshold(e.target.value)} style={input}>
                  <option>Required above ₹5,000</option>
                  <option>Required above ₹3,000</option>
                  <option>Required above ₹10,000</option>
                  <option>Always required</option>
                </select>
              </div>
              {/* Wired checkboxes — no longer hardcoded */}
              <div style={{ paddingTop: 16, borderTop: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 16 }}>
                <ToggleRow
                  checked={approvalRules.receiptMandatory}
                  onChange={v => setApprovalRules(p => ({ ...p, receiptMandatory: v }))}
                  label="Receipt mandatory above ₹500"
                  sub="Require attachment for claims exceeding threshold"
                />
                <ToggleRow
                  checked={approvalRules.gpsMandatory}
                  onChange={v => setApprovalRules(p => ({ ...p, gpsMandatory: v }))}
                  label="GPS / Trip note mandatory for travel"
                  sub="Require location or trip details for travel claims"
                />
                <ToggleRow
                  checked={approvalRules.duplicateDetect}
                  onChange={v => setApprovalRules(p => ({ ...p, duplicateDetect: v }))}
                  label="Duplicate bill detection ON"
                  sub="Auto-flag potential duplicate submissions"
                />
              </div>
            </div>
          </div>

          {/* Exception rules — tags now removable & state-driven */}
          <div style={card}>
            <h3 style={hSection}><i className="fa-solid fa-triangle-exclamation" style={{ color: C.purple }} /> Exception rules</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              {exceptionTags.map((x, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500, color: x.c, background: `${x.c}14`, border: `1px solid ${x.c}4d` }}>
                  {x.t}
                  <button type="button" onClick={() => removeException(i)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, opacity: 0.7 }}>
                    <i className="fa-solid fa-xmark" style={{ fontSize: 12 }} />
                  </button>
                </span>
              ))}
            </div>
            <button type="button" style={{ ...btnOutline, width: "100%", justifyContent: "center" }}>
              <i className="fa-solid fa-plus" /> Add exception
            </button>
          </div>
        </div>

        {/* ── Right column ── */}
        <div style={{ gridColumn: "span 3", display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Policy preview */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 20px", display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-eye" style={{ color: C.purple }} /> Policy preview
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ background: C.surfaceSecondary, borderRadius: 14, padding: 16, textAlign: "center" }}>
                <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", margin: "0 0 8px" }}>Employee type</p>
                <p style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{appliesTo.value || "—"}</p>
              </div>
              <div style={{ background: "rgba(134,96,248,0.1)", border: "1px solid rgba(134,96,248,0.3)", borderRadius: 14, padding: 16, textAlign: "center" }}>
                <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", margin: "0 0 8px" }}>Monthly max</p>
                <p style={{ fontSize: 24, fontWeight: 700, color: C.purple, margin: 0 }}>₹{monthlyCap}</p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {CAT_KEYS.map((k, i) => (
                  <div key={i} style={{ background: C.surfaceSecondary, borderRadius: 8, padding: 12, textAlign: "center" }}>
                    <p style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", margin: "0 0 4px" }}>{k}</p>
                    <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>₹{categories[k]?.monthly || "0"}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Impact analysis — from API or hidden until data arrives */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 20px", display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-chart-line" style={{ color: C.purple }} /> Impact analysis
            </h3>
            {impactData ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ textAlign: "center", padding: "16px 0", background: C.surfaceSecondary, borderRadius: 14 }}>
                  <p style={{ fontSize: 13, color: C.muted, margin: "0 0 8px" }}>Employees affected</p>
                  <p style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>{impactData.employeesAffected ?? "—"}</p>
                </div>
                <div style={{ background: "rgba(134,96,248,0.05)", border: "1px solid rgba(134,96,248,0.2)", borderRadius: 14, padding: 16 }}>
                  <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", margin: "0 0 8px" }}>Est. monthly exposure</p>
                  <p style={{ fontSize: 22, fontWeight: 700, color: C.purple, margin: 0 }}>{impactData.monthlyExposure ?? "—"}</p>
                </div>
                {(impactData.previousLimit || impactData.increase) && (
                  <div style={{ paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                    {impactData.previousLimit && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                        <span style={{ fontSize: 13, color: C.muted }}>Previous limit</span>
                        <span style={{ fontSize: 15, fontWeight: 600 }}>{impactData.previousLimit}</span>
                      </div>
                    )}
                    {impactData.increase && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: C.muted }}>Increase</span>
                        <span style={{ fontSize: 15, fontWeight: 600, color: "#22c55e" }}>{impactData.increase}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "20px 0" }}>
                {limitsLoading ? "Loading…" : "Save policy to see impact analysis"}
              </p>
            )}
          </div>

          {/* Policy alerts — from API */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 20px", display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-bell" style={{ color: C.purple }} /> Policy alerts
            </h3>
            {policyAlerts.length === 0 ? (
              <p style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "12px 0" }}>No alerts</p>
            ) : policyAlerts.map(([icon, title, sub], i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: 12, marginBottom: 12, background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8 }}>
                <i className={`fa-solid ${icon}`} style={{ color: "#f59e0b", fontSize: 14, marginTop: 2 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 4px" }}>{title}</p>
                  <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick actions */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 20px" }}>Quick actions</h3>
            {[
              ["fa-clone",     "Clone policy",       undefined],
              ["fa-user-plus", "Assign employees",   openAssignModal],
              ["fa-power-off", "Deactivate",         undefined],
              ["fa-download",  "Download rules",     undefined],
            ].map(([icon, label, onClick], i) => (
              <button key={i} type="button" onClick={onClick} style={{ ...btnOutline, width: "100%", justifyContent: "center", marginBottom: 12 }}>
                <i className={`fa-solid ${icon}`} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Policy history ── */}
      <section style={{ ...card, marginTop: 32, padding: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 24px", display: "flex", alignItems: "center", gap: 12 }}>
          <i className="fa-solid fa-clock-rotate-left" style={{ color: C.purple }} /> Policy history
        </h3>
        {changeHistory.length === 0 ? (
          <p style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "20px 0" }}>No change history yet</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Date", "Action", "User"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 13, fontWeight: 600, color: C.muted, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {changeHistory.map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "16px", fontSize: 13 }}>{r.date}</td>
                    <td style={{ padding: "16px", fontSize: 13 }}>{r.action}</td>
                    <td style={{ padding: "16px", fontSize: 13, color: C.muted }}>{r.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Assign modal ── */}
      {showAssignModal && (
        <>
          <div onClick={() => setShowAssignModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1200, backdropFilter: "blur(4px)" }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1300, width: "min(95vw, 640px)", maxHeight: "85vh", display: "flex", flexDirection: "column", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.7)", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Assign Policy to Employees</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {assignedCount} assigned · {getSavedPolicyKey() || "Save policy to generate key"}
                </div>
              </div>
              <button type="button" onClick={() => setShowAssignModal(false)} style={{ width: 30, height: 30, borderRadius: 8, background: C.surfaceSecondary, border: `1px solid ${C.border}`, color: C.muted, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}` }}>
              <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employee name or ID…" style={{ ...input, borderRadius: 10, fontSize: 13 }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px" }}>
              {empLoading ? (
                <div style={{ padding: 32, textAlign: "center", color: C.muted }}>Loading employees…</div>
              ) : employees.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: C.muted }}>No employees found</div>
              ) : employees.map((emp, i) => (
                <div key={emp.staffId || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                  <img src={emp.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name || "E")}&background=8660F8&color=fff`} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{emp.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{emp.employeeId} · {emp.department} · {emp.designation}</div>
                  </div>
                  <button type="button" onClick={() => handleToggleAssign(emp.staffId, emp.assigned)} disabled={assigningStaffId !== null}
                    style={{ padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: emp.assigned ? "1px solid rgba(34,197,94,0.35)" : `1px solid ${C.purple}`, background: emp.assigned ? "rgba(34,197,94,0.1)" : "rgba(134,96,248,0.1)", color: emp.assigned ? "#22c55e" : C.purple }}>
                    {assigningStaffId === Number(emp.staffId) ? "Saving…" : (emp.assigned ? "Assigned ✓" : "Assign")}
                  </button>
                </div>
              ))}
            </div>
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, textAlign: "right" }}>
              <button type="button" onClick={() => setShowAssignModal(false)} style={btnPrimary}>Done</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PolicyLimitsEditor;